<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Analys & Statistik</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Överblick av dina AI-agenters prestanda och användarfeedback
        </p>
      </div>
      <div class="mt-4 sm:mt-0 flex items-center space-x-3">
        <select
          v-model="selectedDays"
          @change="fetchAnalytics"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option :value="7">Senaste 7 dagar</option>
          <option :value="30">Senaste 30 dagar</option>
          <option :value="90">Senaste 90 dagar</option>
        </select>
        <button
          @click="fetchAnalytics"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          Uppdatera
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>

    <!-- Stats Cards -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <!-- Total Conversations -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Konversationer</p>
            <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ analytics.summary?.total_conversations || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Total Messages -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
            <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Meddelanden</p>
            <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ analytics.summary?.total_messages || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Positive Feedback -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
            <svg class="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Positiv feedback</p>
            <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ analytics.feedback?.total_positive || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Negative Feedback -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-lg bg-red-100 dark:bg-red-900/30">
            <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Negativ feedback</p>
            <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ analytics.feedback?.total_negative || 0 }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Satisfaction Rate -->
    <div v-if="!loading" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Nöjdhetsgrad</h2>
      <div class="flex items-center space-x-4">
        <div class="flex-1">
          <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              class="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
              :style="{ width: satisfactionRate + '%' }"
            ></div>
          </div>
        </div>
        <span class="text-2xl font-bold text-gray-900 dark:text-white">{{ satisfactionRate }}%</span>
      </div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Baserat på {{ totalFeedback }} feedbacksvar
      </p>
    </div>

    <!-- Additional Stats -->
    <div v-if="!loading" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Average Messages per Conversation -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Genomsnitt per konversation</h2>
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <span class="text-gray-600 dark:text-gray-400">Meddelanden</span>
            <span class="text-xl font-semibold text-gray-900 dark:text-white">
              {{ (analytics.summary?.avg_messages_per_conversation || 0).toFixed(1) }}
            </span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-gray-600 dark:text-gray-400">Nöjdhetsbetyg</span>
            <span class="text-xl font-semibold text-gray-900 dark:text-white">
              {{ (analytics.summary?.avg_satisfaction || 0).toFixed(1) }} / 5
            </span>
          </div>
        </div>
      </div>

      <!-- Resolution Rate -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lösningsgrad</h2>
        <div class="flex items-center space-x-4">
          <div class="relative w-24 h-24">
            <svg class="w-24 h-24 transform -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="currentColor"
                stroke-width="8"
                fill="none"
                class="text-gray-200 dark:text-gray-700"
              />
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="currentColor"
                stroke-width="8"
                fill="none"
                class="text-blue-600"
                :stroke-dasharray="`${resolutionRate * 2.51} 251`"
              />
            </svg>
            <span class="absolute inset-0 flex items-center justify-center text-xl font-bold text-gray-900 dark:text-white">
              {{ resolutionRate }}%
            </span>
          </div>
          <div class="flex-1">
            <p class="text-gray-600 dark:text-gray-400">
              {{ analytics.summary?.resolved_conversations || 0 }} av {{ analytics.summary?.total_conversations || 0 }} konversationer markerades som lösta
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Daily Activity Chart (Simple) -->
    <div v-if="!loading && analytics.daily && analytics.daily.length > 0" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Daglig aktivitet</h2>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Datum</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Konversationer</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Meddelanden</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">👍</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">👎</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="day in analytics.daily.slice(0, 10)" :key="day.date">
              <td class="px-4 py-3 text-sm text-gray-900 dark:text-white">{{ formatDate(day.date) }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{{ day.total_conversations }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{{ day.total_messages }}</td>
              <td class="px-4 py-3 text-sm text-green-600 dark:text-green-400">{{ day.positive_feedback || 0 }}</td>
              <td class="px-4 py-3 text-sm text-red-600 dark:text-red-400">{{ day.negative_feedback || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && (!analytics.daily || analytics.daily.length === 0)" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
      </svg>
      <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">Ingen data ännu</h3>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Statistik visas här när dina AI-agenter börjar användas.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { analyticsService } from '../services/analytics'

interface DailyAnalytics {
  date: string
  total_conversations: number
  total_messages: number
  positive_feedback: number
  negative_feedback: number
  avg_response_time_ms: number
}

interface Analytics {
  daily: DailyAnalytics[]
  summary: {
    total_conversations: number
    total_messages: number
    avg_messages_per_conversation: number
    resolved_conversations: number
    avg_satisfaction: number
  }
  feedback: {
    total_positive: number
    total_negative: number
  }
}

const loading = ref(true)
const selectedDays = ref(30)
const analytics = ref<Analytics>({
  daily: [],
  summary: {
    total_conversations: 0,
    total_messages: 0,
    avg_messages_per_conversation: 0,
    resolved_conversations: 0,
    avg_satisfaction: 0
  },
  feedback: {
    total_positive: 0,
    total_negative: 0
  }
})

const totalFeedback = computed(() => {
  return (analytics.value.feedback?.total_positive || 0) + (analytics.value.feedback?.total_negative || 0)
})

const satisfactionRate = computed(() => {
  const total = totalFeedback.value
  if (total === 0) return 0
  return Math.round((analytics.value.feedback?.total_positive || 0) / total * 100)
})

const resolutionRate = computed(() => {
  const total = analytics.value.summary?.total_conversations || 0
  if (total === 0) return 0
  return Math.round((analytics.value.summary?.resolved_conversations || 0) / total * 100)
})

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' })
}

const fetchAnalytics = async () => {
  loading.value = true
  try {
    console.log('Fetching analytics for', selectedDays.value, 'days...')
    const result = await analyticsService.getAnalytics(selectedDays.value)
    console.log('Analytics result:', result)
    if (result.success && result.data) {
      analytics.value = result.data
      console.log('Analytics updated:', analytics.value)
    } else {
      console.error('Analytics fetch failed:', result.error || result.message)
    }
  } catch (error) {
    console.error('Failed to fetch analytics:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchAnalytics()
})
</script>

